import {
  AppHeader,
  DashboardLayout as UiDashboardLayout,
  SIDEBAR_WIDTH_CLOSED,
  SIDEBAR_WIDTH_OPEN,
  Sidebar,
  SidebarNavItem,
  type SidebarNavItemConfig,
  UserMenuTrigger,
  UserProfilePopover,
  getActiveNavId,
  getAvatarInitials,
  navIcons,
} from '@dawn/ui';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { GlobalFilterRow } from '@/components/GlobalFilterRow';
import { SessionExpiredModal } from '@/components/SessionExpiredModal';
import { SessionWarningBanner } from '@/components/SessionWarningBanner';
import { GLOBAL_FILTER_NAV_IDS, GLOBAL_FILTERS } from '@/config/filters.config';
import {
  FOOTER_NAV_ITEM,
  PRIMARY_NAV_ITEMS,
  resolveNavItems,
  type NavItemConfig,
} from '@/config/navigation';
import { useConfigQuery } from '@/hooks/queries/use-config-query';
import { authApi } from '@/lib/api';
import { SESSION_CHANNEL_NAME } from '@/providers/SessionProvider';
import { useSessionStore } from '@/stores/session';
import { useSidebarStore } from '@/stores/sidebar';

const DEFAULT_PAGE_TITLE = 'Overview';

// Icons render at their natural viewBox sizes (matching Figma per-icon dims)
// bounded by these caps so the 24×24 container keeps visual padding.
const ICON_IMG_CLASS = 'max-w-[20px] max-h-[20px]';

function iconImg(src: string | undefined): React.ReactNode {
  if (!src) return undefined;
  return <img src={src} alt="" className={ICON_IMG_CLASS} />;
}

function toSidebarItem(item: NavItemConfig): SidebarNavItemConfig {
  const icons = navIcons[item.iconId];
  return {
    id: item.id,
    label: item.label,
    icon: iconImg(icons.default),
    iconActive: iconImg(icons.pressed),
    iconDisabled: iconImg(icons.disabled),
    path: item.path,
    enabled: item.enabled,
  };
}

export function DashboardLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setUnauthenticated } = useSessionStore();
  const { isOpen: sidebarOpen, toggle: toggleSidebar } = useSidebarStore();

  const [isProfilePopoverOpen, setIsProfilePopoverOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Already resolved and cached by ConfigGate, which blocks render until it is.
  const { data: config } = useConfigQuery();
  const navItems = useMemo(
    () => resolveNavItems(PRIMARY_NAV_ITEMS, config?.policy.featureFlags),
    [config?.policy.featureFlags]
  );

  const sidebarItems = useMemo(() => navItems.map(toSidebarItem), [navItems]);
  const footerItem = useMemo(() => toSidebarItem(FOOTER_NAV_ITEM), []);

  const allNavItems = useMemo(() => [...navItems, FOOTER_NAV_ITEM], [navItems]);

  const activeItemId = useMemo(
    () => getActiveNavId(location.pathname, allNavItems),
    [location.pathname, allNavItems]
  );

  const pageTitle = useMemo(
    () => allNavItems.find((item) => item.id === activeItemId)?.label ?? DEFAULT_PAGE_TITLE,
    [activeItemId, allNavItems]
  );

  // The global time-frame filter row is opt-in per page via the allow-list in
  // filters.config. The Access Legitimacy page is intentionally excluded — it
  // owns its own filter rail (sensitivity / search / user-type / file-type).
  const showGlobalFilters = GLOBAL_FILTER_NAV_IDS.has(activeItemId ?? '');

  const sidebarWidth = sidebarOpen ? SIDEBAR_WIDTH_OPEN : SIDEBAR_WIDTH_CLOSED;

  const initials = user ? getAvatarInitials(user) : '';
  const displayName = user?.name ?? '';
  const email = user?.email ?? '';

  const handleNavigate = useCallback(
    (item: SidebarNavItemConfig) => {
      if (!item.path) return;
      navigate(item.path);
    },
    [navigate]
  );

  const handleTriggerClick = useCallback(() => {
    setIsProfilePopoverOpen((prev) => !prev);
  }, []);

  const handleClosePopover = useCallback(() => {
    setIsProfilePopoverOpen(false);
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // Ignore errors — cookie may already be invalid
    }
    const logoutChannel = new BroadcastChannel(SESSION_CHANNEL_NAME);
    logoutChannel.postMessage({ type: 'logout' });
    logoutChannel.close();
    setUnauthenticated();
    navigate('/login');
  }, [navigate, setUnauthenticated]);

  return (
    <>
      <SessionWarningBanner />
      <UiDashboardLayout
        sidebarWidth={sidebarWidth}
        filters={showGlobalFilters ? <GlobalFilterRow filters={GLOBAL_FILTERS} /> : undefined}
        sidebar={
          <Sidebar
            items={sidebarItems}
            activeItemId={activeItemId}
            onNavigate={handleNavigate}
            isOpen={sidebarOpen}
            onToggle={toggleSidebar}
            footer={
              <FooterSettingsItem
                item={footerItem}
                active={activeItemId === footerItem.id}
                collapsed={!sidebarOpen}
                onNavigate={handleNavigate}
              />
            }
          />
        }
        header={
          <AppHeader
            title={pageTitle}
            userSlot={
              <>
                <UserMenuTrigger
                  ref={triggerRef}
                  initials={initials}
                  email={email}
                  open={isProfilePopoverOpen}
                  onClick={handleTriggerClick}
                  data-testid="user-menu-trigger"
                />
                <UserProfilePopover
                  isOpen={isProfilePopoverOpen}
                  onClose={handleClosePopover}
                  triggerRef={triggerRef}
                  initials={initials}
                  displayName={displayName}
                  email={email}
                  onLogout={handleLogout}
                />
              </>
            }
          />
        }
      >
        <Outlet />
      </UiDashboardLayout>
      <SessionExpiredModal />
    </>
  );
}

interface FooterSettingsItemProps {
  item: SidebarNavItemConfig;
  active: boolean;
  collapsed: boolean;
  onNavigate: (item: SidebarNavItemConfig) => void;
}

function FooterSettingsItem({ item, active, collapsed, onNavigate }: FooterSettingsItemProps) {
  return (
    <SidebarNavItem
      icon={item.icon}
      iconActive={item.iconActive}
      iconDisabled={item.iconDisabled}
      label={item.label}
      active={active}
      disabled={!item.enabled}
      collapsed={collapsed}
      onClick={() => onNavigate(item)}
    />
  );
}
